/* @jsxImportSource react */
'use client';

import {
  createContext,
  type FocusEventHandler,
  forwardRef,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
} from 'react';

import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { useObservability } from '@opendoor/observability/slim';

import { type AnalyticsProps } from '../../helpers/analytics';
import { cn } from '../../utils/cn';
import { Col } from '../box/box';
import { Field } from '../field/field';

/** Radio variant definitions mapping variant names to their Tailwind classes. */
export const ATOMS_RADIO_VARIANTS = {
  variant: {
    default: {
      classes: 'ring-od-border-primary',
      description: 'Default radio appearance',
    },
    error: {
      classes: 'ring-od-border-error',
      description: 'Error state for validation failures',
    },
  },
  appearance: {
    default: {
      classes: '',
      description: 'Standard inline radio item',
    },
    card: {
      // Static card chrome only — the rendered card branch below consumes this
      // directly and layers the conditional hover/disabled/error refinements on
      // top, so the registry documents exactly what renders.
      classes:
        'rounded-lg border border-od-border-primary bg-od-bg-primary p-3 transition-colors has-data-checked:border-od-border-focused-primary has-data-checked:bg-od-bg-inactive-tertiary',
      description: 'Choice card appearance with border, padding, and highlighted selection state',
    },
  },
} as const;

export const ATOMS_RADIO_DEFAULT_VARIANTS = {
  variant: 'default',
  appearance: 'default',
} as const;

// Derived types from ATOMS_RADIO_VARIANTS
/** @public */
export type AtomsRadioVariant = keyof typeof ATOMS_RADIO_VARIANTS.variant;
/** @public */
export type AtomsRadioAppearance = keyof typeof ATOMS_RADIO_VARIANTS.appearance;

/** @public */
export interface AtomsRadioVariantsProps {
  /**
   * Visual variant.
   * - `"default"` — Standard radio appearance
   * - `"error"` — Error state for validation failures
   * @default "default"
   */
  variant?: AtomsRadioVariant;
  /**
   * Visual appearance.
   * - `"default"` — Standard inline radio item
   * - `"card"` — Choice card with border, padding, and highlighted selection state
   * @default "default"
   */
  appearance?: AtomsRadioAppearance;
}

/** @public */
export function radioVariants({
  variant = ATOMS_RADIO_DEFAULT_VARIANTS.variant,
  appearance = ATOMS_RADIO_DEFAULT_VARIANTS.appearance,
}: AtomsRadioVariantsProps = {}) {
  return cn(
    ATOMS_RADIO_VARIANTS.variant[variant].classes,
    ATOMS_RADIO_VARIANTS.appearance[appearance].classes,
  );
}

// Legacy type alias for backwards compatibility
/** @public */
export type RadioVariant = AtomsRadioVariant;

// Journey requires `option_name` on `radioClickEvent` and drops the event when
// it is empty. Same mask Journey uses for redacted fields.
const REDACTED_OPTION_NAME = 'REDACTED';

// Context for passing appearance and validation state from Group to Items. The
// radio control always sits to the left of its label.
const RadioGroupContext = createContext<{
  appearance: AtomsRadioAppearance;
  variant: AtomsRadioVariant;
}>({
  appearance: 'default',
  variant: 'default',
});

/**
 * Radio group component props.
 *
 * `Radio.Group` renders an accessible group (`role="radiogroup"`) with an
 * optional `label`/`description` above and `error` below — all programmatically
 * associated with the group for assistive tech (it wraps atoms `Field`).
 *
 * @example
 * // Basic usage
 * ```tsx
 * <Radio.Group label="Notification preference" defaultValue="email">
 *   <Radio.Item label="Email" value="email" />
 *   <Radio.Item label="SMS" value="sms" />
 *   <Radio.Item label="Push" value="push" />
 * </Radio.Group>
 * ```
 *
 * @example
 * // Labeled group with an error message
 * ```tsx
 * <Radio.Group label="Payment method" error="Please select a payment method">
 *   <Radio.Item label="Credit Card" value="card" />
 *   <Radio.Item label="PayPal" value="paypal" />
 * </Radio.Group>
 * ```
 *
 * @example
 * // Controlled
 * ```tsx
 * const [value, setValue] = useState("email");
 * <Radio.Group label="Contact via" value={value} onValueChange={setValue}>
 *   <Radio.Item label="Email" value="email" />
 *   <Radio.Item label="Phone" value="phone" />
 * </Radio.Group>
 * ```
 */
// `trackImpression` is intentionally omitted: group view tracking is not wired
// (the group root is a context provider, not a DOM node), so accepting it would
// be a silent no-op. `analyticsName` (for click events) is kept.
export interface RadioGroupProps extends Omit<AnalyticsProps, 'trackImpression'> {
  /** Child Radio.Item components */
  children: ReactNode;
  /**
   * Group label rendered above the items and programmatically associated with
   * the group via `aria-labelledby` (Base UI's RadioGroup picks it up from the
   * surrounding `Field`). Strongly preferred over rendering your own heading,
   * which isn't associated with the group for assistive tech.
   */
  label?: ReactNode;
  /**
   * Supporting text rendered beneath the label, associated with the group via
   * `aria-describedby` so assistive tech announces it.
   */
  description?: ReactNode;
  /**
   * Marks the group required (only meaningful alongside `label`). A
   * screen-reader-only "(required)" is appended to the label by default; pass
   * `false` to render a visible "(optional)" marker instead.
   */
  required?: boolean;
  /**
   * Visual appearance applied to all Radio.Item children.
   * - `"default"` — Standard inline radio items
   * - `"card"` — Choice card with border, padding, and highlighted selection state
   *
   * Individual items can override this with their own `appearance` prop.
   * @default "default"
   */
  appearance?: AtomsRadioAppearance;
  /**
   * Error message for the group. Rendered (with the standard error icon) below
   * the items and associated with the group via `aria-describedby`, so assistive
   * tech announces it.
   */
  error?: string;
  /**
   * Whether the group is in an error state — reddens every item's control and
   * sets `aria-invalid` on the group (which also lets `Form.Root`'s
   * scroll-to-first-error find it). Independent of the `error` message text;
   * defaults to `true` whenever an `error` message is present. Set it directly
   * (without `error`) when the message is rendered elsewhere.
   */
  invalid?: boolean;
  /** Value of the radio that should be initially selected (uncontrolled) */
  defaultValue?: string;
  /** Value of the radio that should be selected (controlled) */
  value?: string;
  /** Event handler called when radio value changes */
  onValueChange?: (value: string) => void;
  /** Called when focus leaves the group — `Form.Radio` wires touched state here. */
  onBlur?: FocusEventHandler<HTMLDivElement>;
  /** Whether all radios in the group are disabled */
  disabled?: boolean;
  /** Form submission name for the radio group */
  name?: string;
  /**
   * Set `false` when the chosen value is the user's answer to a sensitive
   * question (marital status, bankruptcy, citizenship). `radioClickEvent` still
   * fires, with `optionName` set to `"REDACTED"`.
   * @default true
   */
  trackOptionValue?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Individual radio item within a group
 *
 * @example
 * ```tsx
 * <Radio.Item label="Option A" value="a" />
 * ```
 *
 * @example
 * // Disabled item
 * ```tsx
 * <Radio.Item label="Unavailable" value="unavailable" disabled />
 * ```
 */
// Declared as an `interface` (not a `type`) so the docs registry's prop
// extractor — which only parses `interface` bodies — picks up the Radio.Item
// props (`RadioGroupProps` is an interface for the same reason).
/** @public */
export interface RadioItemProps {
  /**
   * Visual variant: `"default"` or `"error"` for validation failures. When set
   * on an individual item, overrides the group-level error state. Inherits from
   * the group when omitted.
   */
  variant?: RadioVariant;
  /**
   * Visual appearance of the radio item.
   * - `"default"` — Standard inline radio item
   * - `"card"` — Choice card with border, padding, and highlighted selection state
   *
   * When set on an individual item, overrides the group-level `appearance`.
   * @default "default"
   */
  appearance?: AtomsRadioAppearance;
  /** Label content displayed next to radio (required). Accepts strings or React nodes for rich content. */
  label: ReactNode;
  /** Description text displayed below the label (only visible in card appearance) */
  description?: ReactNode;
  /** Value of the radio (required) */
  value: string;
  /** Additional CSS classes for the label wrapper */
  className?: string;
  /** Whether the radio is disabled */
  disabled?: boolean;
}

// Radio.Item for use within Radio.Group
const RadioItem = forwardRef<HTMLButtonElement, RadioItemProps>(
  (
    {
      className,
      disabled,
      variant: variantProp,
      appearance: appearanceProp,
      label,
      description,
      value,
    },
    ref,
  ) => {
    const { appearance: groupAppearance, variant: groupVariant } = useContext(RadioGroupContext);
    const appearance = appearanceProp ?? groupAppearance;
    const variant = variantProp ?? groupVariant;
    const isCard = appearance === 'card';

    // Name each radio by its own label text. Base UI otherwise resolves the
    // control's accessible name from the nearest Field label (the group label),
    // so inside a `Field` every item would announce as the group
    // name. An explicit `aria-labelledby` takes precedence over that; the card
    // description is wired as `aria-describedby` so it's announced as detail.
    const labelId = useId();
    const descriptionId = useId();
    const describedBy = description ? descriptionId : undefined;

    if (isCard) {
      return (
        <label
          className={cn(
            'group relative flex items-start gap-3',
            ATOMS_RADIO_VARIANTS.appearance.card.classes,
            variant === 'error' &&
              'border-od-border-error has-data-checked:border-od-border-error has-data-checked:bg-od-bg-error-secondary',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : cn(
                  'has-data-disabled:cursor-not-allowed has-data-disabled:opacity-50 cursor-pointer',
                  variant !== 'error' && 'hover:not-has-data-disabled:bg-od-bg-hover-primary',
                ),
            className,
          )}
        >
          <BaseRadio.Root
            ref={ref}
            value={value}
            disabled={disabled}
            aria-labelledby={labelId}
            aria-describedby={describedBy}
            className={cn(
              'relative translate-y-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-0 bg-od-bg-primary ring-2 focus:outline-none focus:ring-od-border-focused-primary focus-visible:ring-2 focus-visible:ring-od-border-focused-accent-brand',
              variant === 'error' ? 'ring-od-border-error' : 'ring-od-border-primary',
              !disabled &&
                variant !== 'error' &&
                'group-hover:ring-od-border-hover-primary focus-visible:outline-offset-3',
              !disabled && variant === 'error' && 'focus-visible:outline-offset-3',
              'data-checked:bg-od-bg-inverse-primary data-checked:ring-od-border-focused-primary',
            )}
          >
            <BaseRadio.Indicator keepMounted className="flex items-center justify-center">
              <span className="h-2 w-2 rounded-full bg-od-bg-primary" />
            </BaseRadio.Indicator>
          </BaseRadio.Root>
          <Col className="min-w-0 flex-1 gap-0.5">
            <span id={labelId} className="text-label-md text-od-content-primary">
              {label}
            </span>
            {description && (
              <span id={descriptionId} className="text-body-sm text-od-content-tertiary">
                {description}
              </span>
            )}
          </Col>
        </label>
      );
    }

    return (
      <label
        className={cn(
          'group relative inline-flex items-center gap-2',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className,
        )}
      >
        <BaseRadio.Root
          ref={ref}
          value={value}
          disabled={disabled}
          aria-labelledby={labelId}
          className={cn(
            'relative flex h-4 w-4 items-center justify-center rounded-full border-0 bg-od-bg-primary ring focus:outline-none after:absolute after:-inset-x-3 after:-inset-y-2',
            variant === 'error' ? 'ring-od-border-error' : 'ring-od-border-primary',
            !disabled &&
              variant !== 'error' &&
              'group-hover:ring-od-border-hover-primary focus:ring-od-border-focused-primary focus:ring-2 focus-visible:ring-2 focus-visible:ring-od-border-focused-accent-brand focus-visible:outline-offset-3',
            !disabled &&
              variant === 'error' &&
              'focus:ring-od-border-focused-primary focus:ring-2 focus-visible:ring-2 focus-visible:ring-od-border-focused-accent-brand focus-visible:outline-offset-3',
            'data-checked:bg-od-bg-inverse-primary data-checked:ring-od-border-focused-primary',
          )}
        >
          <BaseRadio.Indicator keepMounted className="flex items-center justify-center">
            <span className="h-2 w-2 rounded-full bg-od-bg-primary" />
          </BaseRadio.Indicator>
        </BaseRadio.Root>
        <span id={labelId} className="text-body-md text-od-content-primary">
          {label}
        </span>
      </label>
    );
  },
);

RadioItem.displayName = 'Radio.Item';

// Radio.Group — built on Base UI's RadioGroup, wrapped in atoms `Field` so the
// group label, description, and error are programmatically associated with the
// radiogroup (Base UI's RadioGroup reads the surrounding Field's label/error and
// wires `aria-labelledby` / `aria-describedby` / `aria-invalid` automatically).
function RadioGroup({
  children,
  label,
  description,
  required,
  appearance = 'default',
  error,
  invalid,
  defaultValue,
  value,
  onValueChange,
  onBlur,
  disabled,
  name,
  className,
  analyticsName,
  trackOptionValue = true,
}: RadioGroupProps) {
  const { trackJourneyEvent } = useObservability();
  // An explicit `invalid` wins; otherwise the presence of an `error` message
  // implies the error state, so passing `error` alone reddens the controls and
  // marks the group invalid.
  const isInvalid = invalid ?? Boolean(error);
  const variant: AtomsRadioVariant = isInvalid ? 'error' : 'default';
  const context = useMemo(() => ({ appearance, variant }), [appearance, variant]);

  // `analyticsName` belongs on the group, not individual items. Base UI calls
  // onValueChange only on user selection, not programmatic value updates.
  // Guarded: no event without an analyticsName.
  const handleValueChange = useCallback(
    (newValue: string) => {
      if (analyticsName) {
        trackJourneyEvent('platform', 'radioClickEvent', {
          targetName: analyticsName,
          optionName: trackOptionValue ? newValue : REDACTED_OPTION_NAME,
        });
      }
      onValueChange?.(newValue);
    },
    [analyticsName, trackOptionValue, trackJourneyEvent, onValueChange],
  );

  return (
    <RadioGroupContext.Provider value={context}>
      <Field
        label={label}
        description={description}
        required={required}
        error={error}
        invalid={isInvalid}
        className={className}
      >
        <BaseRadioGroup
          defaultValue={defaultValue}
          value={value}
          onValueChange={handleValueChange}
          onBlur={onBlur}
          disabled={disabled}
          name={name}
        >
          <Col className={appearance === 'card' ? 'gap-3' : 'gap-2'}>{children}</Col>
        </BaseRadioGroup>
      </Field>
    </RadioGroupContext.Provider>
  );
}

RadioGroup.displayName = 'Radio.Group';

// Export RadioGroup directly for external usage
/** @public */
export { RadioGroup };

/**
 * Radio — radio button group for single-select choices.
 *
 * Compound component: `Radio.Group` and `Radio.Item`. Built on
 * `@base-ui/react/radio-group` + `@base-ui/react/radio`. Pass `label` to
 * `Radio.Group` for an accessible group label.
 *
 * @example
 * ```tsx
 * <Radio.Group label="Notification preference" defaultValue="email">
 *   <Radio.Item label="Email" value="email" />
 *   <Radio.Item label="SMS" value="sms" />
 *   <Radio.Item label="Push" value="push" />
 * </Radio.Group>
 * ```
 */
export const Radio = Object.assign(RadioGroup, {
  Item: RadioItem,
  Group: RadioGroup,
});
